
import React, { useContext, useEffect, useState, useRef, memo } from "react";
import { unstable_batchedUpdates } from 'react-dom';
import { useHistory, Link } from "react-router-dom";
import Chart from "core/ui/charts/index";
import DualSelect from "partials/dual-select";
import FormatCurrency from "core/utils/FormatCurrency";
import FormatNumber from "core/utils/FormatNumber";
import SessionContext from "SessionContext";
import ComplexFilterBuilder from "./complex-filter-builder";
import ConfirmSuccess from "../core/ui/confirm-success";
import { Button, Modal, Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Row, Col, FormFeedback, Table, ModalHeader, ModalBody, ModalFooter, Alert, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import HasLicenseFee from "core/utils/has-license-fee";
import SimpleDropdown from "../core/ui/simple-dropdown";
import TooltipOnhover from "../partials/tooltip-onhover";
import LZString from 'lz-string';
import debounce from "lodash.debounce";
import classNames from "classnames";
import isEqual from "lodash.isequal";
import dayjs from "dayjs";
import { existingSimpleFilters } from "core/utils/data-helper-functions";
import DraggableList from "../core/ui/draggable-list";
import useFiltersInfo from "../hooks/useFiltersInfo";
import { useGlobalState } from "hooks/useGlobalState";
import InfoPopover from "../core/ui/info-popover";
import OrderFinalizeExternalDestination from "../partials/census-sync/order-finalize-external-destination";
import ListPicker from "partials/list-picker";

const order_limits_enum = {
  per_order: "Per Order",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
}

// channel_options names as returned by the API, used to match against the channelOption keys (direct/email/digital/analytics)
const channel_option_name_match = {
  direct: "direct mail",
  email: "email",
  digital: "custom digital",
  analytics: "analytics",
}

// export default function OrderForm(props) {
const OrderForm = memo((props) => {
  const session = useContext(SessionContext);
  const history = useHistory();
  const filtersInfo = useFiltersInfo(true);
  const { setIsGlobalStateSaved } = useGlobalState();

  let maxQuantity = `${window.max_order_quantity}` !== "undefined" ? `${window.max_order_quantity}` : "50000";
  const minQuantity = `${window.min_order_quantity}` !== "undefined" ? `${window.min_order_quantity}` : "100";

  if(session.appState.activeCompany.enable_order_cost_limit) {
    maxQuantity = Number.MAX_SAFE_INTEGER.toString(); /* make the max higher to allow the cost limit to take effect */
  }

  const [loaded, setLoaded] = useState(false);
  const [updating, setUpdating] = useState(true);

  const [orderName, setOrderName] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [orderDescription, setOrderDescription] = useState("");
  const [notes, setNotes] = useState("");

  const [orderNameError, setOrderNameError] = useState("*Required");
  const [showSuccess, setShowSuccess] = useState(false);
  const [succesLabel, setSuccessLabel] = useState("Saved!");
  const [unsavedModalIsOpen, setUnsavedModalIsOpen] = useState(false);

  const [originalQuantity, setOriginalQuantity] = useState();
  const [orderQuantity, setOrderQuantity] = useState();
  const [desiredQuantity, setDesiredQuantity] = useState();
  const [newDesiredQuantity, setNewDesiredQuantity] = useState(0);
  const [desiredQtyError, setDesiredQtyError] = useState(false);

  const access = session.appState.userCan;

  const [channelOption, setChannelOption] = useState({
    direct: true,
    email: false,
    digital: false,
    analytics: false,
  });
  const [channelOptionError, setChannelOptionError] = useState("");
  const [totalCost, setTotalCost] = useState(0);
  const [activeFilters, setActiveFilters] = useState(null);
  const [allFields, setAllFields] = useState(Object.values(session.chartsStore.orderable));
  const [selectedFields, setSelectedFields] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [isComplex, setIsComplex] = useState();
  const [errorModalIsOpen, setErrorModalIsOpen] = useState(false);
  const [complexFilters, setComplexFilters] = useState();
  const [complexFields, setComplexFields] = useState();
  const [disableOrderBtn, setDisableOrderBtn] = useState(true);
  const [showNoInvoiceModal, setShowNoInvoiceModal] = useState(false);
  const [orderHasChanged, setOrderHasChanged] = useState(false);
  const [originalOrder, setOriginalOrder] = useState({});
  const [hasLicenseFee, setHasLicenseFee] = useState(false);
  const [showNeedLicense, setShowNeedLicense] = useState(false);
  const [isEmailSelected, setIsEmailSelected] = useState(false);
  const [toggleActionsDropdown, setToggleActionsDropdown] = useState(false);
  const [countChangedSinceLastSaved, setCountChangedSinceLastSaved] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [dataFieldsMissing, setDataFieldsMissing] = useState(false);
  const [dataFiltersHaveChanged, setDataFiltersHaveChanged] = useState(false);
  const [noFilters, setNoFilters] = useState(false);
  const [originalOrderBelowMin, setOriginalOrderBelowMin] = useState(false);
  const [deliveryOptionsList, setDeliveryOptionsList] = useState([]);
  const [defaultDeliveryOption, setDefaultDeliveryOption] = useState(false);
  const [apiListLoaded, setApiListLoaded] = useState(false);
  const [hasMyDataOnly, setHasMyDataOnly] = useState(false);
  const savedOrder = useRef();
  const [currentCompanyId, setCurrentCompanyId] = useState(false);
  const [showCassMessage, setShowCassMessage] = useState(false);
  const [showOrderCapDetails, setShowOrderCapDetails] = useState(false);
  const [exceedCapLimit, setExceedCapLimit] = useState(false);
  const [orderLimitsInfo, setOrderLimitsInfo] = useState([]);
  const [orderApproversInfo, setOrderApproversInfo] = useState([]);
  const [isOrderApprover, setIsOrderApprover] = useState(false);
  const [hasApprovedOrder, setHasApprovedOrder] = useState(false);
  const [hasAcknowledgeCheck, setHasAcknowledgeCheck] = useState(false);
  const [isValidQuantity, setIsValidQuantity] = useState(false);
  const [hasExternalDestinationDelivery, setHasExternalDestinationDelivery] = useState(false);
  const [externalDestinations, setExternalDestinations] = useState([]);
  const [selectedExternalDestinations, setSelectedExternalDestinations] = useState([]);
  const [showDestinationPicker, setShowDestinationPicker] = useState(false);
  const [pendingExtDest, setPendingExtDest] = useState(false);
  const [showExtDest, setShowExtDest] = useState(false);
  const [censusSyncData, setCensusSyncData] = useState({});
  const [missingDestinations, setMissingDestinations] = useState([]);
  const [syncCompleted, setSyncCompleted] = useState(false);
  const [isMappingStatus, setIsMappingStatus] = useState(false);
  const [externalDestinationNotAvailable, setExternalDestinationNotAvailable] = useState(false);
  const [hasSyncMappingChanges, setHasSyncMappingChanges] = useState(false);
  const [confirmLeaveSyncModal, setConfirmLeaveSyncModal] = useState(false);
  const pendingSyncAction = useRef(null);
  const isCancellingSyncRef = useRef(false);
  const [showDirectMailMessage, setShowDirectMailMessage] = useState(false);
  const [useLastOrder, setUseLastOrder] = useState(false);


  let originalChannelOptions = useRef([]);
  let unsavedContinuePath = useRef(null);

  //Cutback prioritization
  const [cutbackPriorities, setCutbackPriorities] = useState([]);
  const [cutbackHasChanged, setCutbackHasChanged] = useState(false);

  // review approval
  const [showReviewApproval, setShowReviewApproval] = useState(false);

  const handleShowReviewApprovalModal = () => {
    setShowReviewApproval(!showReviewApproval);
  }

  const handleDenyOrder = (_reason) => {
    onSubmit({ _approved: false, _denied: true, _reason: _reason });
  }

  const handleApproveSubmitOrder = () => {
    onSubmit({ _approved: true, _denied: false, _reason: "" });
  }

  const handleCutbackChanges = (_status) => {
    setCutbackHasChanged(_status);
  }

  const handleDestinationPicker = () => {
    setShowDestinationPicker(!showDestinationPicker);
  }

  // TODO: handle multiple selection to instead of single array element.
  const handleSelectDestination = (item) => {
    if (item) {
      setSelectedExternalDestinations([item]);
    } else {
      setSelectedExternalDestinations([]);
    }
    setMissingDestinations([]);
    setShowDestinationPicker(false);
  }

  const handleCensusData = (data) => {
    setCensusSyncData(data);
  }

  const getCompanyDestinations = () => {
    session.query('census/list-for-company')
      .then(response => {
        if (response) {
          const destinations = response;
          // Update options with fetched destinations
          setExternalDestinations(destinations);
        }
      })
      .catch(error => {
        console.error("Error fetching company destinations:", error);
      });
  };

  // Info content
  const infoData = {
    direct:
    {
      title: "Direct Mail Usage",
      message: "Data may be used one time for Direct Mail."
    },
    email:
    {
      title: "Email Deployment Usage",
      message: "Data may be used one time for Email Acquisition."
    },
    digital:
    {
      title: "Digital Onboarding Usage",
      message: "Data may be used for Digital Onboarding purposes for unlimited digital usage for a period of 30 days."
    },
    analytics:
    {
      title: "Analytics Usage",
      message: "Data may be hosted on client server for a maximum period of 12 months exclusively for the purpose of internal analysis. No marketing use permitted with this use case."
    }
  };

  function checkForChanges() {
    if (!loaded || syncCompleted) { return false }

    if (!originalOrder.name) {
      setOrderHasChanged(true);
      return;
    }
    const fields = originalOrder.fields;
    if (fields.length !== selectedFields.length) {
      setOrderHasChanged(true && getMaxQuantity() >= minQuantity);
      return;
    }

    let fieldsHasChanged = false;

    const selectedIds = new Set(selectedFields.map((item) => item.id));
    fields.forEach((item) => {
      if (!selectedIds.has(item.id)) {
        fieldsHasChanged = true;
      }
    });

    let channelOptionsChange = false;
    if (
      (originalChannelOptions.current.email !== undefined && originalChannelOptions.current.email !== channelOption.email) ||
      (originalChannelOptions.current.digital !== undefined && originalChannelOptions.current.digital !== channelOption.digital) ||
      (originalChannelOptions.current.direct !== undefined && originalChannelOptions.current.direct !== channelOption.direct) ||
      (originalChannelOptions.current.analytics !== undefined && originalChannelOptions.current.analytics !== channelOption.analytics)
    ) {
      channelOptionsChange = true;
    }

    let dataDeliveryChange = false;
    let original_delivery_selected = originalOrder.delivery_options.map(item => item.id).sort();
    let current_delivery_selected = deliveryOptionsList.filter(item => item.checked).map(item => item.id).sort();
    const programmaticChange = deliveryOptionsList.some(item => item.prog);
    if (!isEqual(original_delivery_selected, current_delivery_selected) && !programmaticChange) {      
      dataDeliveryChange = true;
    }

    let externalDestinationsChange = false;
    if (!isEqual((originalOrder.census_destinations || []).sort(), (selectedExternalDestinations || []).sort())) {
      externalDestinationsChange = true;
    }

    const checkCount = () => {
      if (countChangedSinceLastSaved) {
        return false;
      } else {
        return parseInt(desiredQuantity) !== originalOrder.quantity;
      }
    }

    const hasChanged = (
      (
        orderName !== originalOrder.name ||
        orderDescription !== originalOrder.description ||
        checkCount() ||
        notes !== originalOrder.notes ||
        channelOptionsChange ||
        dataFieldsMissing ||
        dataFiltersHaveChanged ||
        fieldsHasChanged ||
        dataDeliveryChange ||
        externalDestinationsChange ||
        cutbackHasChanged
      ) &&
      getMaxQuantity() >= minQuantity
    );

    setOrderHasChanged(hasChanged);
  }

  useEffect(() => {
    setIsOrderApprover(access("order_cost_limit_approval"));
  }, [access]);

  useEffect(() => {
    // set the data delivery options on first load
    const setDataDeliverySelected = (has_id) => {
      let _options_list = [];
      let _default_list = [];
      let _company_id = session.appState.activeCompany.id;

      session.query("order/get_default_delivery_options", { id: _company_id })
        .then((_defaults) => {
          _default_list = _defaults;
        })
        .then(() => {
          session.query("admin/get_delivery_options", {include_populated_only: true})
            .then((_options) => {
              _options.forEach((_option) => {
                _options_list.push({ ..._option, checked: false })
              })
              if(has_id) {
                // check only boxes selected in the saved order
                _options_list.forEach((_option) => {
                  originalOrder.delivery_options.forEach((_saved) => {
                    if (_option.id === _saved.id) {
                      _option.checked = true;
                      _option.prog = true;
                    }
                  })
                })
                setDeliveryOptionsList(_options_list);
              } else {
                if (Object.keys(_default_list)) {
                  // check the boxes set as default for the company
                  _default_list.forEach((val) => {
                    _options_list.forEach((_check) => {
                      if (_check.id === val.id) {
                        _check.checked = true;
                        _check.prog = true; // mark as programmatic change
                      }
                    })
                  })
                }               
                setDeliveryOptionsList(_options_list);
              }
            })
        })
    }
    if (originalOrder && loaded) {
      let has_id = (originalOrder && originalOrder.id) ? true : false;
      setDataDeliverySelected(has_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded])

  useEffect(() => {
    if (loaded) {
      filtersInfo.then((val) => {
        let hasMyDataOnly = true;
        let _selectedFilters = val.all_filters;
        let _allSelectedFiltersIds = [];
        _selectedFilters && _selectedFilters.forEach((val) => {
          _allSelectedFiltersIds.push(val.id);
        })

        // remove filters without selections
        let filtersWithValues = { ...session.crossfilterStore.filters };
        filtersWithValues && Object.keys(filtersWithValues).forEach(key => {
          if (Array.isArray(filtersWithValues[key]) && filtersWithValues[key].length === 0) {
            delete filtersWithValues[key];
          }
        });

        _allSelectedFiltersIds = Object.keys(filtersWithValues).filter(value => _allSelectedFiltersIds.includes(value));

        let _charts = session.chartsStore.taxonomy[1]["My Data"];
        _charts = _charts ? JSON.stringify(_charts[1]) : [];

        _allSelectedFiltersIds && _allSelectedFiltersIds.forEach((val) => {
          if (!_charts.includes(val)) {
            hasMyDataOnly = false;
          }
        })
        setHasMyDataOnly(hasMyDataOnly);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded])

  useEffect(() => {
    if (deliveryOptionsList.length && loaded && !apiListLoaded) {
      getApiList(session.appState.activeCompany.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, deliveryOptionsList])

  const getApiList = (_company_id) => {
    let url = `companies/list_api_keys?am_company_id=${_company_id}&filters=%7B%7D&sort_by=&sort_dir=desc`
    session.query(url)
      .then((_apis) => {
        if (_apis.rows.length) {
          // check api expiration
          let hasValidApi = false;
          _apis.rows.forEach((val) => {
            if (dayjs().isBefore(dayjs(val.expiration_date).add(1, 'day'))) {
              hasValidApi = true;
            }
          })
          if (hasValidApi) {
            getIpList(_company_id);
          } else {
            removeApiOption(true);
          }
        } else {
          removeApiOption(true);
        }
        setApiListLoaded(true);
      })
  }

  const getIpList = (_company_id) => {
    let url = `companies/list_ip_white_lists?am_company_id=${_company_id}&filters=%7B%7D&sort_by=&sort_dir=desc`
    session.query(url)
      .then((_ips) => {
        if (!_ips.rows.length) {
          removeApiOption(true);
        }
      })
  }

  const removeApiOption = (_remove = false) => {
    if (deliveryOptionsList.length) {
      let updated_list = [...deliveryOptionsList];
      const index = updated_list.findIndex(item => item.name === 'API');
      if (index !== -1) {
        // Remove 'API' from the list
        updated_list.splice(index, 1);
      }
      _remove && setDeliveryOptionsList(updated_list);
    }
  }

  useEffect((prevValue) => {
    checkForChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderName,
    orderDescription,
    orderQuantity,
    notes,
    selectedFields,
    channelOption,
    desiredQuantity,
    newDesiredQuantity,
    cutbackHasChanged
  ]
  );

  function validateQuantity(value) {
    const audienceCount = session.crossfilterStore.count;
    const val = parseInt(value);
    const min = parseInt(minQuantity);
    const max = parseInt(maxQuantity);
    let valid = true;

    if (val < min) {
      valid = false;
      value = minQuantity;
    } else if (val > audienceCount || val > max) {
      valid = false;
      value = getMaxQuantity();
    }
    setIsValidQuantity(valid);
    return { valid: valid, value: value };
  }

  function getMaxQuantity() {
    const count = session.crossfilterStore.count || originalQuantity;
    if (count === 0) return;
    let value = (count > maxQuantity) ? maxQuantity : count;
    return value;
  }


  function onOrderNameChange(ev) {
    setOrderName(ev.target.value);
  }
  function onOrderDescriptionChange(ev) {
    setOrderDescription(ev.target.value);
  }
  function onNotesChange(ev) {
    setNotes(ev.target.value);
  }

  function handleOnDesiredQuantityChange(ev) {
    const newValue = ev.target.value.replace(/\D/, '');
    const validQTY = validateQuantity(ev.target.value);
    setCountChangedSinceLastSaved(false);
    setDesiredQtyError(false);
    setNewDesiredQuantity(newValue);
    setDesiredQuantity(newValue);
    if (!validQTY.valid) {
      setDesiredQtyError(true);
    }
  }

  function onChannelOptionChange(ev) {
    if (!loaded) { return; }
    // if analytics was checked unset the others
    if (ev.target.id === "analytics") {
      setChannelOption({ direct: false, email: false, digital: false, analytics: true });
      return;
    } else {
      setChannelOption((prevValue) => {
        prevValue[ev.target.value] = ev.target.checked;
        prevValue.analytics = false;
        return { ...prevValue };
      });
    }
  }

  function onDeliveryOptionChange(_id) {
    if (!loaded) { return; }
    let updateList = [];
    deliveryOptionsList.forEach((_val) => {
      delete _val.prog; // remove programmatic change marker as this is user-initiated
      if (_val.id === _id) {
        _val.checked = !_val.checked;
      }
      updateList.push(_val);
    })
    setDeliveryOptionsList(updateList);
  }

  useEffect(() => {
    setHasExternalDestinationDelivery(deliveryOptionsList.some(option => option.checked && option.id === 4)); // check for External Destination
    checkForChanges();
    // eslint-disable-next-line
  }, [deliveryOptionsList]);

  useEffect(() => {
    setPendingExtDest(hasExternalDestinationDelivery && originalOrder?.status === "Pending Approval");
  }, [hasExternalDestinationDelivery, originalOrder]);

  function onSetDeliveryOptionDefault(ev) {
    setDefaultDeliveryOption(ev.target.checked);
  }

  useEffect(() => {
    if (channelOption.email || channelOption.digital) {
      // check if x-filters has the 'email mandatory' selected
      setIsEmailSelected(session.crossfilterStore.hasMandatoryEmail());
    } else {
      setIsEmailSelected(false);
    }

    setAvailableFields();
    // eslint-disable-next-line
  }, [channelOption?.email, channelOption?.digital])

  useEffect(() => {
    if (
      loaded &&
      !originalOrder?.id &&
      !originalQuantity
    ) {
      setOriginalQuantity(session.crossfilterStore.count && session.crossfilterStore.count >= minQuantity ? session.crossfilterStore.count : minQuantity);
    } else if (loaded && originalOrder?.id && !originalQuantity) {
      setOriginalQuantity(originalOrder.original_quantity);
    }
  },
    // eslint-disable-next-line
    [
      loaded,
      originalOrder
    ]);

  useEffect(() => {
    if (
      loaded &&
      orderQuantity
    ) {
      setDesiredQuantity(validateQuantity(orderQuantity).value);
      getCompanyDestinations();
    }
  },
    // eslint-disable-next-line
    [
      loaded,
      orderQuantity,
    ]);

  useEffect(() => {
    if (newDesiredQuantity !== desiredQuantity) {
      setNewDesiredQuantity(desiredQuantity || orderQuantity || originalQuantity);
    }
    // eslint-disable-next-line
  }, [desiredQuantity])

  const debounce_getOrderQuote = debounce(() => { getOrderQuote() }, 500);

  function checkIfLimitExceeded(_data) {
    if (_data.order_limit_excesses && _data.order_limit_excesses.length) {
      setExceedCapLimit(true);
      setOrderLimitsInfo(_data.order_limit_excesses);
      setOrderApproversInfo(_data.company_approvers);
    } else {
      setExceedCapLimit(false);
      setOrderLimitsInfo([]);
      setOrderApproversInfo([]);
    }
  }

  function getOrderQuote() {
    session.crossfilterStore.quote_order(
      {
        quantity: desiredQuantity,
        channel_option: channelOption,
        fields: selectedFields,
        cutback_fields: cutbackPriorities
      },
      (response) => {
        setIsComplex(session.crossfilterStore.isComplex);
        checkIfLimitExceeded(response);
        if (isComplex) {
          setComplexFilters(session.crossfilterStore.complexFilters);
          session.crossfilterStore
            .getComplexFields()
            .then((_complexFields) => {
              setComplexFields(_complexFields);
            });
        }
        setLineItems(response.invoice_line_items);
        setTotalCost(response.total_cost);
        setUpdating(false);
        if (!response.include_consolidated_cass_ncoa_report) {
          showCassNcoaWarning();
        }
      },
      (_error) => {
        setUpdating(false);
      }
    );
  }


  function showCassNcoaWarning() {
    session.command("order/display_cassncoa_warning",
        { filter:session.crossfilterStore.filters,
          channel_option: channelOption,
          company_id: session.appState.activeCompany.id,
        }).then((_data) => {
                if (_data != null) {
                  setShowCassMessage(_data.payload);
                } else {
                  console.error("Could not determine whether to show CASS/NCOA warning.");
                }
        });
  }

  useEffect(() => {
    if (loaded && desiredQuantity) {
      setUpdating(true);
      if (!Object.values(channelOption).reduce((v, acc) => acc || v, false)) {
        // Disable ordering if no options are checked
        setLineItems([]);
        setTotalCost(0);
        setUpdating(false);
      } else {
        debounce_getOrderQuote();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, desiredQuantity, orderQuantity, selectedFields, channelOption]);


  // Main Order load and setup function which can be called outside of page load.
  function loadOrder(id){
      session.query("order/get", { id: id }).then((_order) => {
        if (_order.status !== "Created" && _order.status !== "Pending Approval" && _order.status !== "Mapping") {
          history.push(`/order-details#order=${props.args.order}`);
        }
        // reset status if status is "Mapping" to allow edits
        if (_order.status === "Mapping"){
          _order.status = "Created";
        }
        setOriginalOrderBelowMin(getMaxQuantity() < minQuantity);
        savedOrder.current = JSON.parse(JSON.stringify(_order));
        setOriginalOrder(_order);
        setOrderQuantity(_order.quantity);
        setOrderName(_order.name);
        setOrderNumber(_order.id);
        setOrderDescription(_order.description);
        setNotes(_order.notes);
        setChannelOption(_order.channel_option);
        setDeliveryOptionsList(_order.delivery_options);
        setHasExternalDestinationDelivery(_order.delivery_options.some(option => option.id === 4)); // check for External Destination
        setSelectedExternalDestinations(_order.census_destinations || []);
        setIsMappingStatus(_order.status === "Mapping");
        originalChannelOptions.current = { ..._order.channel_option };
        setIsComplex(_order.is_complex);
        session.crossfilterStore.set_missing_filters(false);
        if (_order.is_complex) {
          setComplexFilters(_order.complex_filters);
          session.crossfilterStore.getComplexFields().then((_complexFields) => {
            setComplexFields(_complexFields);
          });
        }

        for (const [key, values] of Object.entries(_order.filters)) {
          session.crossfilterStore.set_filters(key, values);
        }

        setSelectedFields(
          allFields.filter((a) =>
            (_order.fields || []).find((f) =>
              a.ds === f.ds &&
              a.id === f.id &&
              // filter out email and digital
              (a.channel !== "email" &&
                a.channel !== "digital"))
          )
        );

        // check if selected data fields are still available
        if (_order && _order.fields.length) {
          setDataFieldsMissing(checkDataFieldsExist(_order.fields));
        }

        let availableFilters;
        if (_order.is_complex) {
          availableFilters = session.crossfilterStore.checkComplexFiltersExist(_order.complex_filters);
          _order.complex_filters = availableFilters;
        }
        setLoaded(true);
      });
  }

  useEffect(() => {
    if( Object.keys(session.crossfilterStore.filters).length && Object.keys(originalOrder).length ) {
      let temp_filters = Object.entries(originalOrder.filters)
                       .filter(([_, filters]) => !!filters)
                       .map(([chart_id, _]) => chart_id);
      setActiveFilters(temp_filters);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalOrder, session.crossfilterStore.filters])

  useEffect(() => {
    if (props.args && props.args.order) {
      if (!sessionStorage.getItem('orderHasReloaded')) {
        // get the correct population data when first load an order
        sessionStorage.setItem('orderHasReloaded', 'true');
        window.location.reload();
        return;
      }
      loadOrder(props.args.order);
    } else {
      // if no counts because of unsaved order (no id) and page reload we need to load it from the last audience state
      // this triggers the useEffect below to actually load the page.
      let lastOrder = JSON.parse(LZString.decompress(localStorage.getItem("am:order:last_state")) || "{}");
      if (!session.crossfilterStore.count) {
        session.crossfilterStore.restoreFromAudience({
          ...lastOrder,
          id: undefined,
        });
        setUseLastOrder(true);
      } else {
        // first page load from a new audience (this works because crossfilterStore has the audience counts)
        session.crossfilterStore.count && setOrderQuantity(validateQuantity(session.crossfilterStore.count).value);
        if (session.crossfilterStore.isComplex) {
          setIsComplex(session.crossfilterStore.isComplex);
          setComplexFilters(session.crossfilterStore.complexFilters);
          session.crossfilterStore.getComplexFields().then((_complexFields) => {
            setComplexFields(_complexFields);
          });
        }

        setTimeout(() => {
          setLoaded(true);
        }, 1000);
        
      }
    }
    session.crossfilterStore.set_no_filters(false);

    return () => sessionStorage.removeItem('orderHasReloaded');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!useLastOrder) return;

    const _lastOrder = JSON.parse(
      LZString.decompress(localStorage.getItem("am:order:last_state")) || "{}"
    );

    if (!Object.keys(_lastOrder).length) {
      setLoaded(true);
      return;
    }

    unstable_batchedUpdates(() => {
      if (_lastOrder.desiredQuantity)           setNewDesiredQuantity(_lastOrder.desiredQuantity);
      if (_lastOrder.originalQuantity)          setOriginalQuantity(_lastOrder.originalQuantity);
      if (_lastOrder.orderQuantity)             setOrderQuantity(_lastOrder.orderQuantity);
      if (_lastOrder.orderName)                 setOrderName(_lastOrder.orderName);
      if (_lastOrder.orderDescription)          setOrderDescription(_lastOrder.orderDescription);
      if (_lastOrder.notes)                     setNotes(_lastOrder.notes);
      if (_lastOrder.channelOption)             setChannelOption(_lastOrder.channelOption);
      if (_lastOrder.selectedFields?.length)    setSelectedFields(_lastOrder.selectedFields);
      if (_lastOrder.deliveryOptionsList?.length) setDeliveryOptionsList(_lastOrder.deliveryOptionsList);
      if (_lastOrder.defaultDeliveryOption !== undefined) setDefaultDeliveryOption(_lastOrder.defaultDeliveryOption);
      if (_lastOrder.cutbackPriorities?.length) setCutbackPriorities(_lastOrder.cutbackPriorities);
      if (_lastOrder.hasApprovedOrder !== undefined) setHasApprovedOrder(_lastOrder.hasApprovedOrder);
      if (_lastOrder.hasExternalDestinationDelivery !== undefined) setHasExternalDestinationDelivery(_lastOrder.hasExternalDestinationDelivery);
      if (_lastOrder.selectedExternalDestinations?.length) setSelectedExternalDestinations(_lastOrder.selectedExternalDestinations);
    });

    setTimeout(() => setLoaded(true), 1000);
  }, [useLastOrder]);

  useEffect(() => {
    if( Object.keys(session.crossfilterStore?.filters).length ) {
      let temp_filters = Object.entries(session.crossfilterStore.filters)
                      .filter(([_, filters]) => !!filters)
                      .map(([chart_id, _]) => chart_id);
      setActiveFilters(temp_filters);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.crossfilterStore?.filters])

  useEffect(() => {
    const handleBeforeUnload = () => {
      const orderData = {
        ...session.crossfilterStore.audience,
        desiredQuantity,
        originalQuantity,
        orderQuantity,
        orderName,
        orderDescription,
        notes,
        channelOption,
        selectedFields,
        deliveryOptionsList,
        defaultDeliveryOption,
        cutbackPriorities,
        hasApprovedOrder,
        hasExternalDestinationDelivery,
        selectedExternalDestinations,
      };
      localStorage.setItem("am:order:last_state", LZString.compress(JSON.stringify(orderData)));
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    desiredQuantity, originalQuantity, orderName, orderDescription,
    notes, channelOption, selectedFields, deliveryOptionsList,
    defaultDeliveryOption, cutbackPriorities, hasApprovedOrder,
    hasExternalDestinationDelivery, selectedExternalDestinations, orderQuantity
  ]);

  // sets count on page reload and after restored from audience
  useEffect(() => {
    if (!Object.keys(originalOrder).length && session.crossfilterStore.count) {
      setOrderQuantity(validateQuantity(session.crossfilterStore.count).value);
      if (session.crossfilterStore.audience.is_complex) {
        setIsComplex(true);
        setComplexFilters(session.crossfilterStore.audience.complex_filters);
        session.crossfilterStore.getComplexFields().then((_complexFields) => {
          setComplexFields(_complexFields);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.crossfilterStore.count]);

  // To run after everything is actually loaded to check for missing features.
  useEffect(() => {
    if (loaded && session.crossfilterStore.loaded && session.crossfilterStore.audienceLoaded && savedOrder.current) {
      let missing = false;
      let availableFilters;
      let charts = session.chartsStore.charts;
      if (!charts) return;
      if (savedOrder.current.is_complex) {
        availableFilters = session.crossfilterStore.checkComplexFiltersExist(savedOrder.current.complex_filters);
        missing = dataFiltersHaveChanged || !isEqual(availableFilters.tree.children1, savedOrder.current.complex_filters.tree.children1);
        if (!noFilters) {
          setNoFilters(availableFilters && !Object.keys(availableFilters.tree.children1).length);
          setDataFiltersHaveChanged(missing);
          return;
        }
      } else {
        if (!savedOrder.current.filters) return;
        charts = Object.entries(session.chartsStore.charts);
        availableFilters = existingSimpleFilters(savedOrder.current.filters, charts, availableFilters);
        missing = !isEqual(availableFilters, Object.entries(savedOrder.current.filters));
        if (!noFilters) {
          const no_filters = (availableFilters && !Object.keys(availableFilters).length) ? true : false;
          setNoFilters(no_filters);
          setDataFiltersHaveChanged(missing);
        }
      }
      setDataFieldsMissing(checkDataFieldsExist(savedOrder.current.fields));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, session.crossfilterStore.loaded, session.crossfilterStore.audienceLoaded]);

  // check for any missing filters
  useEffect(() => {
    if (!session.crossfilterStore.audienceLoaded) return;
    setDataFiltersHaveChanged(session.crossfilterStore.missingComplexFilters || session.crossfilterStore.missingFilters);
  }, [
    session.crossfilterStore.audienceLoaded,
    session.crossfilterStore.missingFilters,
    session.crossfilterStore.missingComplexFilters,
  ]);

  // Check that data fields that are selected still exist.
  function checkDataFieldsExist(_fields) {
    if (!_fields.length) return false;
    let fieldExists;
    fieldExists = allFields.filter((a) => (_fields || []).find((f) => (a.ds === f.ds && a.id === f.id)));
    const missingFields = (fieldExists.length !== _fields.length);
    return missingFields;
  }

  function cancelExternalDestination(path) {
    isCancellingSyncRef.current = true;
    resetOrderStatus()
      .then(() => {
        setCensusSyncData(null);
        history.push(path);
      })
      .catch(error => {
        console.error("Error cancelling external destination sync creation:", error);
      })
      .finally(() => {
        isCancellingSyncRef.current = false;
      });
  }

  // This effect halts in app page router navigation to warn about unsaved data
  useEffect(() => {
    const unblock = history.block((location, action) => {
      if (currentCompanyId && (session.appState.activeCompany.id !== currentCompanyId)) {
        return true;
      } else if (showExtDest) {
        // If already cancelling (stale closure re-entry), let the navigation through
        if (isCancellingSyncRef.current) return true;
        if (hasSyncMappingChanges) {
          pendingSyncAction.current = () => cancelExternalDestination(location.pathname);
          setConfirmLeaveSyncModal(true);
        } else {
          cancelExternalDestination(location.pathname);
        }
        return false;
      } else if (orderHasChanged && unsavedContinuePath.current !== location.pathname && !noFilters) {
        // check if changes have been made and the location has changed. The location gets reset with a modal toggle. 
        unsavedContinuePath.current = location.pathname;
        setUnsavedModalIsOpen(true)
        return false;
      } else {
        return true;
      }
    });

    return () => {
      unblock();
    };
    // eslint-disable-next-line
  }, [history, orderHasChanged, noFilters, showExtDest, censusSyncData, hasSyncMappingChanges]);

  useEffect(() => {
    if(orderHasChanged && !noFilters) {
      localStorage.setItem("am:order:has_unsaved_changes", "true");
    } else {
      localStorage.setItem("am:order:has_unsaved_changes", "false");
    }
    return () => localStorage.setItem("am:order:has_unsaved_changes", "false");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderHasChanged])

  // Unsaved Modal continue handler
  function continueWithoutSaving() {
    // use ref stored when modal was opened with current intended path
    history.push(unsavedContinuePath.current);
  }

  function onChangeTemplate(values) {
    if (!values.length) {
      setSelectedFields([]);
    } else {
      let findCode = [];
      values.length && values.forEach((code) => {
        allFields.forEach((option) => {
          if (option.id === code) {
            findCode.push(option);
          }
        });
      })
      if (findCode.length) {
        setSelectedFields(findCode);
      } else {
        setSelectedFields([]);
      }
    }
  }

  function onSave() {
    if (!orderName) {
      setOrderNameError("*Required");
    } else {
      setOrderNameError("");
      setUpdating(true);
      session.crossfilterStore.save_order(
        {
          id: !!props.args.order ? props.args.order : null,
          quantity: desiredQuantity,
          original_quantity: originalQuantity,
          name: orderName,
          description: orderDescription,
          channel_option: channelOption,
          fields: selectedFields,
          notes: notes,
          delivery_options: deliveryOptionsList.filter(item => item.checked),
          change_default_delivery_options: defaultDeliveryOption,
          cutback_fields: cutbackPriorities,
          cost_limit_excess_approved: hasApprovedOrder,
          ...(hasExternalDestinationDelivery && selectedExternalDestinations.length && { census_destinations: selectedExternalDestinations }),
        },
        (res) => {
          setOrderHasChanged(false);
          setShowSuccess(true);
          setSuccessLabel("Order Saved.");
          setUpdating(false);
          setTimeout(() => {
            setShowSuccess(false);
            window.location.href = `/order#order=${res.id}`;
          }, 2000);
        },
        (error) => {
          setUpdating(false);
          console.log("Failed to Save Order", JSON.stringify(error));
        }
      );
    }
  }

  function onFinalize() {
    if (!orderName) {
      setOrderNameError("*Required");
    } else {
      setOrderNameError("");
      session.crossfilterStore.save_order(
        {
          id: !!props.args.order ? props.args.order : originalOrder.id || null,
          quantity: desiredQuantity,
          original_quantity: originalQuantity,
          name: orderName,
          description: orderDescription,
          channel_option: channelOption,
          fields: selectedFields,
          notes: notes,
          delivery_options: deliveryOptionsList.filter(item => item.checked),
          change_default_delivery_options: defaultDeliveryOption,
          cutback_fields: cutbackPriorities,
          cost_limit_excess_approved: hasApprovedOrder,
          status: "Mapping",
          ...(hasExternalDestinationDelivery && selectedExternalDestinations.length && { census_destinations: selectedExternalDestinations }),
        },
        (res) => {
          res.filter = session.crossfilterStore.getFilterJson(); // necessary for census order table creation.
          setSelectedExternalDestinations(res.census_destinations);
          setOriginalOrder(res)
          setOrderHasChanged(false);
          setUpdating(false);
          setShowExtDest(true);
        },
        (error) => {
          console.log("Failed to Save Order", JSON.stringify(error));
        }
      );
    }
  }

  function handleExtDestExit(completed, sync_id) {
    if (!completed && hasSyncMappingChanges) {
      pendingSyncAction.current = () => proceedWithExtDestExit(completed, sync_id);
      setConfirmLeaveSyncModal(true);
      return;
    }
    proceedWithExtDestExit(completed, sync_id);
  }

  function proceedWithExtDestExit(completed, sync_id) {
    setShowExtDest(false);
    setHasSyncMappingChanges(false);
    if (completed) {
      setSyncCompleted(true);
      setOrderHasChanged(false);
      onSubmit();
    } else {
      resetOrderStatus(sync_id);
    }
  }

  const handleConfirmLeaveSync = () => {
    setConfirmLeaveSyncModal(false);
    if (pendingSyncAction.current) {
      pendingSyncAction.current();
      pendingSyncAction.current = null;
    }
  };
  
  // reset order status to "Created" on incomplete sync setup to allow for order editing.
  function resetOrderStatus(){
    if(originalOrder?.status !== "Mapping") return Promise.resolve();
    setIsMappingStatus(false);
    setShowExtDest(false);
    setSyncCompleted(false);
    return session.command(`census/reset-order?am_order_id=${originalOrder?.id}`)
      .then(() => {
        // reset open order to "Created" status to allow editing of open order without reload.
        setOriginalOrder({...originalOrder, status: "Created"});
      })
      .catch(error => {
          console.log("Failed resetting order:", error);
    });
  }

  useEffect(() => {
    if (selectedExternalDestinations?.length && externalDestinations?.length) {
      let invalidSelections = [];
      invalidSelections = selectedExternalDestinations.filter(sel => !externalDestinations.find(ext => ext.id === sel.id));

      if (invalidSelections.length) {
        setMissingDestinations(invalidSelections);
      }
    }
    checkForChanges();
    // eslint-disable-next-line
  }, [selectedExternalDestinations, externalDestinations]);

  useEffect(() => {
    if(originalOrder.status === "Mapping") { 
      setIsMappingStatus(true);
    }
  }, [originalOrder.status]);

  useEffect(() => {
    /* this check should run only when an order is saved, in 'created' status and it has external destinations selected */
    if(externalDestinations && (originalOrder?.status === "Created")) {
      let has_external_dest_selected = originalOrder.delivery_options.some(option => option.id === 4);
      if(has_external_dest_selected) {
        let selected_id = originalOrder.census_destinations?.map(item => item.id);
        let available_ids = externalDestinations.map(item => item.id);

        let exists = selected_id?.some(id => available_ids.includes(id));
        if(!exists && selected_id?.length) {
          setExternalDestinationNotAvailable(true);
          setSelectedExternalDestinations([]);
        } else {
          setExternalDestinationNotAvailable(false);
        }
      }
    }
    // eslint-disable-next-line
  }, [externalDestinations]);

  function onSubmit({ _approved = false, _denied = false, _reason = "" } = {}) {
    if (!hasLicenseFee) {
      setShowNeedLicense(true);
    } else if (!orderName) {
      setOrderNameError("Required");
    } else {
      setOrderNameError("");
      setUpdating(true);

      // handle save cutback
      let cutback_to_save = {};
      if (cutbackPriorities && cutbackPriorities.length) {
        let charts_info = session.chartsStore.charts;
        let selected_id = cutbackPriorities[0].id;
        let selected_chart = charts_info[selected_id];
        let chart_values = [];
        let selected_values = [];
        let sorted_selected_values = [];

        // handle 'models'
        /**
         * @ model_: matches the literal string "model_"
         * @ ([1-9][0-9]{0,5}): matches numbers from 1 to 999999
         * @ [0-9]{0,5}: allows up to 5 more digits, making the total number range from 1 to 6 digits
         */
        const ModelMatchRegex = /^model_([1-9][0-9]{0,5})$/;
        if (selected_chart && selected_chart.id && ModelMatchRegex.test(selected_chart.id)) {
          cutback_to_save = { label: `Model: ${selected_chart.label}`, values: [], ds: selected_chart.ds, id: selected_chart.id }
        } else { // handle all other filters
          if (selected_chart) {
            // handle geo charts
            if (selected_chart.ds === `${session.crossfilterStore.geoPrefix}geo`) {
              selected_chart.value_labels && Object.values(selected_chart.value_labels).forEach((val) => {
                sorted_selected_values.push(val);
              })
            } else {
              chart_values = selected_chart.value_labels;
              cutbackPriorities.forEach((_item) => {
                if (_item.values) {
                  selected_values.push(Object.keys(_item.values)[0])
                } else {
                  selected_values.push(_item.value)
                }
              })
              selected_values.forEach((item) => {
                let _value = chart_values[item];
                if (item) {
                  if (_value) {
                    sorted_selected_values.push(chart_values[item]);
                  } else {
                    sorted_selected_values.push(item);
                  }
                }
              })
            }

            cutback_to_save = {
              label: selected_chart.label,
              values: sorted_selected_values,
              id: selected_id,
              ds: selected_chart.ds
            };
          }
        }
      }

      let build_props = {
        id: props.args.order || originalOrder.id || null,
        quantity: desiredQuantity,
        original_quantity: originalQuantity,
        name: orderName,
        description: orderDescription,
        saved_selection_criteria: originalOrder.saved_selection_criteria,
        channel_option: channelOption,
        fields: selectedFields,
        notes: notes || "",
        delivery_options: deliveryOptionsList.filter(item => item.checked),
        change_default_delivery_options: defaultDeliveryOption,
        cutback_fields: cutbackPriorities,
        saved_cutback_field_prioritization: cutback_to_save,
        cost_limit_excess_approved: hasApprovedOrder,
        ...(hasExternalDestinationDelivery && selectedExternalDestinations.length && { census_destinations: selectedExternalDestinations }),
      }

      let order_success_actions = () => {
        setOrderHasChanged(false);
        setUpdating(false);
        setShowSuccess(true);
        setSuccessLabel(_denied ? "Order Denied." : "Order Submitted.");
        setTimeout(() => {
          history.push('/orders');
        }, 2000);
      }

      let review_info = { approved: false, denied: false, reason: _reason };

      let finalize_order = () => {
        session.crossfilterStore.submit_order(
          build_props,
          order_success_actions(),
          (error) => {
            setUpdating(false);
            if (error && Object.keys(error).length > 0) {
              console.log(`Error: ${JSON.stringify(error)}`);
            }
          },
          review_info
        );
      }

      if (_approved) {
        review_info = {
          ...review_info,
          approved: true,
          denied: false,
        };
        finalize_order();
        return;
      } else if (_denied) {
        review_info = {
          ...review_info,
          approved: false,
          denied: true,
        };
        finalize_order();
        return;
      } else {
        finalize_order();
      }
    }
  }

  function onSubmitNoInvoice(reason) {
    setShowNoInvoiceModal(false);
    let orderData = {
      order_id: props.args.order,
      reason
    }

    session.command("order/save_order_do_not_invoice",
      orderData,
      {}, { defaultErrorHandling: false })
      .then(() => {
        setShowSuccess(true);
        setSuccessLabel("Order Submitted.");
        setTimeout(() => {
          history.push('/orders');
        }, 2000);
      })
      .catch((error) => {
        console.log("Failed to Submit Order", JSON.stringify(error));
      })
  }

  function toggleModal(event) {
    event.preventDefault();
    setErrorModalIsOpen(!errorModalIsOpen);
  }

  function toggleUnsavedModal(event) {
    //sets the continue path to null after viewing
    unsavedContinuePath.current = null;
    setUnsavedModalIsOpen(!unsavedModalIsOpen)
  }

  function toggleOrderCapDetails() {
    setShowOrderCapDetails(!showOrderCapDetails);
  }

  function handleApproveCheckbox(e) {
    const checked = e.target.checked;
    setHasAcknowledgeCheck(checked);
  }

  function handleApproveOrder() {
    setHasApprovedOrder(true);
    toggleOrderCapDetails();
  }

  function handleRemoveApproval() {
    setHasApprovedOrder(false);
    setHasAcknowledgeCheck(false);
    toggleOrderCapDetails();
  }

  function toggleDescriptionExpand(e) {
    const desc = e.target;
    if (e.type === "focus") {
      desc.setAttribute("rows", 4);
    } else {
      desc.setAttribute("rows", 1);
    }
  }

  // Helper function to filter out channel fields of type "email" or "digital"
  function setAvailableFields() {
    let availableFields, selectFields = [];
    availableFields = session.chartsStore.orderable.filter((field) =>
      (field.channel !== "email" && field.channel !== "digital"));
    selectFields = selectedFields.filter((field) =>
      (field.channel !== "email" && field.channel !== "digital"));

    availableFields.length && setAllFields(availableFields);
    selectFields.length && setSelectedFields(selectFields);
  }

  useEffect(() => {
    function checkChannelOptions() {
      let isEmpty = true;
      let channelOpt = Object.values(channelOption);
      channelOpt.forEach((value) => { if (value) isEmpty = false });
      if (isEmpty) {
        setChannelOptionError("Required")
      } else {
        setChannelOptionError("");
      }

      return isEmpty;
    }

    function checkOrderName() {
      if (!orderName || orderName.length < 1) {
        setOrderNameError("*Required");
        return true;
      }
      setOrderNameError("")
      return false;
    }

    function checkDataDelivery() {
      let current_delivery_selected = deliveryOptionsList.filter((val) => val.checked === true);
      if (current_delivery_selected.length < 1) {
        return true;
      } else {
        return false;
      }
    }

    let isChannelOptionEmpty = checkChannelOptions();
    let isOrderNameEmpty = checkOrderName();
    let isDataDeliveryEmpty = checkDataDelivery();

    if (isChannelOptionEmpty || isOrderNameEmpty || isDataDeliveryEmpty) {
      setDisableOrderBtn(true);
    } else {
      setDisableOrderBtn(false);
    }

  }, [orderName, channelOption, deliveryOptionsList]);

  const hasLicense = (value) => {
    setHasLicenseFee(value || false);
  }

  const openActionsDropdown = (e) => {
    e.preventDefault();
    setToggleActionsDropdown(true);
  }

  const hideActionsDropdown = (e) => {
    e.preventDefault();
    setToggleActionsDropdown(false);
  }

  const cutbackPrioritySelection = (_data) => {
    setCutbackPriorities(_data);
  }

  const renderOrderAction = () => {
    const pending = (originalOrder.status === "Pending Approval") ? true : false;
    const needsApproval = (exceedCapLimit && !hasApprovedOrder);
    const hasExtDestSync = (pending && hasExternalDestinationDelivery) ? true : false;
    const disabled = disableOrderBtn || (hasExternalDestinationDelivery && !selectedExternalDestinations.length) || showExtDest || !isValidQuantity || pending || getMaxQuantity() < minQuantity || updating;

    if (pending) {
      return "";
    }

    if (!hasExternalDestinationDelivery) {
      if (!needsApproval) {
        return (
          <DropdownItem onClick={onSubmit}
            disabled={disabled}>
            <i className="bi bi-send" /> Place Order
          </DropdownItem>
        );
      } else {
        return (
          <DropdownItem onClick={onSubmit}
            disabled={disabled}>
            <i className="bi bi-send" /> Place Order for Approval
          </DropdownItem>
        );
      }
    } else {
      if (!needsApproval) {
        if (!hasExtDestSync) {
          return (
            <DropdownItem onClick={onFinalize}
              disabled={disabled}>
              <i className="bi bi-send" /> Finalize Order
            </DropdownItem>
          );
        } else if (hasExtDestSync) {
          return (
            <DropdownItem onClick={onSubmit}
              disabled={disabled}>
              <i className="bi bi-box-arrow-up" /> Place Order
            </DropdownItem>
          );
        }
      } else {
        if (!hasExtDestSync) {
          return (
            <DropdownItem onClick={onFinalize}
              disabled={disabled}>
              <i className="bi bi-box-arrow-up" /> Finalize Order for Approval
            </DropdownItem>
          );
        } else if (hasExtDestSync) {
          return (
            <DropdownItem onClick={onSubmit}
              disabled={disabled}>
              <i className="bi bi-box-arrow-up" /> Place Order for Approval
            </DropdownItem>
          );
        }
      }
    }
  }

  const renderDestinationStatus = (id) => {
    const missing = missingDestinations?.find(dest => dest.id === id);
    if (!missing) {
      if (originalOrder.status === "Pending Approval") {
        return <span className="status-success">Fields mapped</span>
      } else {
        return <span className="status-warning">Fields not mapped</span>
      }
    } else {
      return <span className="status-error">Destination deleted or disabled</span>
    }
  }

  useEffect(() => {
    // check if the last saved count has changed: show message if it has
    if(!originalOrder?.quantity) {
      setCountChangedSinceLastSaved(false);
      return;
    } else if((desiredQuantity > 0) && desiredQuantity !== originalOrder.quantity && initialLoad) {
      setCountChangedSinceLastSaved(true);
      setInitialLoad(false);
      setUpdating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, desiredQuantity, originalOrder.quantity])

  useEffect(() => {
    // keep global sync with local
    setIsGlobalStateSaved(!orderHasChanged);
    return () => {
      // when leaving component reset global
      setIsGlobalStateSaved(true);
    };
    // eslint-disable-next-line
  }, [orderHasChanged])

  useEffect(() => {
    if (currentCompanyId && (session.appState.activeCompany.id !== currentCompanyId)) {
      setUnsavedModalIsOpen(false);
      setOrderHasChanged(false);
    }
    // eslint-disable-next-line
  }, [session.appState.activeCompany.id])

  useEffect(() => {
    if(!loaded) {return}
    let _filters = session.crossfilterStore.filters;
    let direct_mail_ids = ["direct_mail_all_00001", "direct_mail_all_00003", "direct_mail_all_00005"];

    // check if direct mail is being used
    let has_direct_mail_in_filters = direct_mail_ids.filter(id =>
      Object.prototype.hasOwnProperty.call(_filters, id) &&
      Array.isArray(_filters[id])
    );

    // check if direct mail is actually selected
    let show_direct_mail_message = has_direct_mail_in_filters.length > 0 && has_direct_mail_in_filters.every(id => _filters[id].length === 0);

    if(channelOption.direct && !has_direct_mail_in_filters.length) {
      setShowDirectMailMessage(true);
    } else if(channelOption.direct && show_direct_mail_message) {
      setShowDirectMailMessage(true);
    } else {
      setShowDirectMailMessage(false);
    }
  // eslint-disable-next-line
  }, [channelOption?.direct, loaded])

  useEffect(() => {
    setCurrentCompanyId(session.appState.activeCompany.id);
    // eslint-disable-next-line
  }, []);

  const order_name = (orderNumber !== "") ? `${orderName} - #${orderNumber}` : "";

  if (!loaded) { return <div className="page m-3">Loading...</div> }

  return (
    <div className="page">
      {updating && (
        <div className="overlay">
          <div className="loader" />
        </div>
      )}
      <div className="list-header sticky-page-header flex-header">
        <div className="header-link-back">
          <Link to="/orders">
            <Button type="button" className="new-button">
              <i className="bi bi-chevron-left"></i> Orders
            </Button>
          </Link>
        </div>
        <div className="header-name">
          <TooltipOnhover labelText={order_name} />
        </div>
        {!showExtDest &&
        <div className="order-actions-dropdown">
          <Dropdown
            className="dd-page-actions"
            isOpen={toggleActionsDropdown}
            toggle={hideActionsDropdown}
            onMouseEnter={openActionsDropdown}
            onMouseLeave={hideActionsDropdown}
          >
            <DropdownToggle size="sm" caret
              className={orderHasChanged ? "promote" : ""}
            >
              Order Actions
            </DropdownToggle>
            <DropdownMenu>

              {access('order_edit') &&
                <DropdownItem
                  onClick={onSave}
                  disabled={disableOrderBtn || !orderHasChanged || !isValidQuantity || showExtDest || updating}
                  className="promote"
                >
                  <i className="bi bi-save2" /> {(orderHasChanged) ? "Save Order" : "Order Saved"}
                </DropdownItem>
              }

              {access('order_place') &&
                <>
                  {renderOrderAction()}
                </>
              }

              {access('order_cost_limit_approval') && (originalOrder.status === "Pending Approval") && exceedCapLimit && isValidQuantity &&
                <DropdownItem
                  onClick={handleShowReviewApprovalModal}
                  disabled={disableOrderBtn || updating}
                  className="promote"
                >
                  <i className="bi bi-exclamation-circle" /> Review Approval
                </DropdownItem>
              }

            </DropdownMenu>
          </Dropdown>
        </div>
        }
      </div>

      <div className="page order-page-wrapper">
        <section className="main-content">
          <div className="order">
            {!showExtDest || !Object.keys(originalOrder).length ? (
              <Card>
                <CardHeader>
                  <Form className='bs-form order-form order-name-desc' onSubmit={e => e.preventDefault()}>
                    <Row>
                      <Col md={4}>
                        <FormGroup>
                          <Label for="order-name">Order Name:<sup>*</sup></Label>
                          <Input
                            id="order-name"
                            type="text"
                            value={orderName}
                            invalid={orderNameError !== ""}
                            onChange={onOrderNameChange}
                            disabled={!access('order_edit')}
                          >
                          </Input>
                          <FormFeedback invalid={orderNameError.toString()}>
                            {orderNameError}
                          </FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={8}>
                        <FormGroup>
                          <Label for="order-description">Description:</Label>
                          <Input
                            id="order-description"
                            type="textarea"
                            rows="1"
                            value={orderDescription}
                            onChange={onOrderDescriptionChange}
                            onFocus={toggleDescriptionExpand}
                            onBlur={toggleDescriptionExpand}
                            disabled={!access('order_edit')}
                          >
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                </CardHeader>
                <CardBody>
                  <h2>Order Options</h2>
                  <Form className="order-options bs-form" onSubmit={e => e.preventDefault()}>
                    <FormGroup>
                      <Label for="desired-qty">Desired Quantity:</Label>
                      <Input
                        type="text"
                        id="desired-qty"
                        value={newDesiredQuantity}
                        min={minQuantity}
                        max={maxQuantity}
                        onChange={handleOnDesiredQuantityChange}
                        disabled={!access('order_edit') || getMaxQuantity() < minQuantity}
                      />
                      {getMaxQuantity() >= minQuantity && !updating &&
                        <label className={classNames("requirement", { "error": desiredQtyError })}> (quantity must be between <strong>{minQuantity}</strong> and <strong>{getMaxQuantity()}</strong>)</label>
                      }
                    </FormGroup>
                    {props.args.order && (
                      <>
                        <Alert
                          isOpen={
                            countChangedSinceLastSaved &&
                            getMaxQuantity() >= minQuantity &&
                            getMaxQuantity() !== maxQuantity &&
                            loaded && !updating &&
                            !orderHasChanged}
                          color="warning"
                          className="d-flex"
                        >
                          <i className="bi bi-exclamation-triangle-fill"></i>
                          <div>
                            The available quantity of this audience has changed since it was last saved.
                            <ul>
                              <li>
                                Last saved count: <strong>{originalOrder.quantity}</strong>.
                              </li>
                              <li>
                                Currently available: <strong>{getMaxQuantity()}</strong>.
                              </li>
                            </ul>
                          </div>
                        </Alert>
                      </>
                    )}

                  </Form>

                  {desiredQuantity ?
                    <CutbackPriorityGroup
                      onSelection={cutbackPrioritySelection}
                      data={originalOrder.cutback_fields}
                      desiredQuantity={desiredQuantity}
                      originalQuantity={originalQuantity}
                      onChanges={handleCutbackChanges}
                      _filtersInfo={filtersInfo}
                      _session={session}
                    />
                    : ""}

                  <div className="bs-form mt-4">
                    <Label>
                      Channel Layout Options:<sup>*</sup>
                      <span className="form-field-error regular-font-size">{channelOptionError}</span>
                    </Label>
                    <Form className="form-controls radio" onSubmit={e => e.preventDefault()}>
                      <FormGroup inline>
                        <input
                          type="checkbox"
                          value="direct"
                          checked={channelOption["direct"]}
                          onChange={onChannelOptionChange}
                          disabled={!access('order_edit')}
                        />
                        <label className="nowrap" htmlFor="direct">
                          Direct Mail&nbsp;
                        </label>
                        <InfoPopover
                          title={infoData.direct.title}
                          message={infoData.direct.message}
                          offsetTop={0}
                        />
                      </FormGroup>
                      <FormGroup inline>
                        <input
                          type="checkbox"
                          value="email"
                          checked={channelOption["email"]}
                          onChange={onChannelOptionChange}
                          disabled={!access('order_edit')}
                        />
                        <label className="nowrap" htmlFor="email">
                          Email Deployment&nbsp;
                        </label>
                        <InfoPopover
                          title={infoData.email.title}
                          message={infoData.email.message}
                          offsetTop={0}
                        />
                      </FormGroup>
                      <FormGroup inline>
                        <input
                          type="checkbox"
                          value="digital"
                          checked={channelOption["digital"]}
                          onChange={onChannelOptionChange}
                          disabled={!access('order_edit')}
                        />
                        <label className="nowrap" htmlFor="digital">
                          Digital Onboarding&nbsp;
                        </label>
                        <InfoPopover
                          title={infoData.digital.title}
                          message={infoData.digital.message}
                          offsetTop={0}
                        />
                      </FormGroup>
                      <div className="h-divider"></div>
                      <FormGroup inline>
                        <div className="v-divider"></div>
                        <input
                          type="checkbox"
                          value="analytics"
                          id="analytics"
                          checked={channelOption["analytics"]}
                          onChange={onChannelOptionChange}
                          disabled={!access('order_edit')}
                        />
                        <label className="nowrap" htmlFor="analytics">
                          Analytics Only&nbsp;
                        </label>
                        <InfoPopover
                          title={infoData.analytics.title}
                          message={infoData.analytics.message}
                          offsetTop={0}
                        />
                      </FormGroup>
                    </Form>
                    <Alert
                      isOpen={isEmailSelected}
                      color="light"
                      className="d-flex"
                    >
                      <i className="bi bi-info-circle-fill"></i>
                      <div>
                        Email will only be included where available. If you only want records with an email address present, return to <b className="light-text-color">Explorer</b> and adjust selections in the <b className="light-text-color">Email Channel Options</b>.
                      </div>
                    </Alert>
                    <Alert
                      isOpen={channelOption.email}
                      color="light"
                      className="d-flex"
                    >
                      <i className="bi bi-info-circle-fill"></i>
                      <div>
                        Email quantities are estimates only. Once the final order is placed, additional email hygiene will be run. Email quantities will drop by an estimated 20% to 30%.
                      </div>
                    </Alert>
                    <Alert
                      isOpen={channelOption.analytics}
                      color="light"
                      className="d-flex"
                    >
                      <i className="bi bi-info-circle-fill"></i>
                      <div>
                        Analytics use of a file cannot be ordered at the same time as marketing lists. If you wish to order this audience for marketing, please select other channel options.
                      </div>
                    </Alert>

                  </div>
                  <Alert isOpen={pendingExtDest || isMappingStatus} color="warning" className="d-flex">
                    <i className="bi bi-info-circle-fill"></i>
                    <div>
                      The <strong>External Destinations</strong> and <strong>Additional Data Fields</strong> below cannot be edited because an external destination sync has been created for this order.
                    </div>
                  </Alert>
                  <Alert isOpen={showDirectMailMessage} color="light" className="d-flex">
                    <i className="bi bi-info-circle-fill"></i>
                    <div>
                      To make sure your order is ready for direct mail delivery, return to <strong>Explorer</strong> and filter for "<strong>Direct Mail Ready</strong>" under <strong>Channel Options</strong> before placing your order.
                    </div>
                  </Alert>
                  <div className="bs-form">
                    <Label>
                      Data Delivery Options:<sup>*</sup>
                      {deliveryOptionsList.filter((val) => val.checked === true).length < 1 && <span className="form-field-error regular-font-size">Required</span>}
                    </Label>
                    <Alert
                      isOpen={missingDestinations?.length && !pendingExtDest}
                      color="warning"
                      className="d-flex"
                    >
                      <i className="bi bi-exclamation-triangle-fill"></i>
                      <div>
                        Since the last time this order was opened, one or more external destinations below are no longer available. Please review and either select a new destination or uncheck the "External Destinations" delivery option.
                      </div>
                    </Alert>
                    <Form className="form-controls radio" onSubmit={e => e.preventDefault()}>
                      {(apiListLoaded && deliveryOptionsList) && deliveryOptionsList.map((_option) => {
                        return (
                          <FormGroup inline key={_option.id} disabled={pendingExtDest}>
                            <input
                              type="checkbox"
                              value={_option.name}
                              checked={_option.checked}
                              onChange={() => onDeliveryOptionChange(_option.id)}
                              disabled={!access('order_edit') || ((pendingExtDest || isMappingStatus) && _option.id === 4)}
                            />
                            <label className="nowrap">
                              {_option.name}
                              {_option.id === 4 && _option.checked && !pendingExtDest && !isMappingStatus && (
                                <button
                                  type="button"
                                  title="Edit External Destination"
                                  className="btn btn-sm sub-company-options-btn promote-link"
                                  onClick={handleDestinationPicker}
                                  style={{ marginLeft: 8 }}
                                >
                                  {selectedExternalDestinations.length === 0 ? (
                                    <>
                                      <i className="bi bi-plus" /> Select External Destination
                                    </>
                                  ) : (
                                    <>
                                      <i className="bi bi-pencil" /> Edit External Destination
                                    </>
                                  )}
                                </button>
                              )}
                            </label>
                          </FormGroup>
                        )
                      })}
                      <ListPicker
                        isOpen={showDestinationPicker}
                        toggle={handleDestinationPicker}
                        onConfirm={(item) => handleSelectDestination(item)}
                        items={externalDestinations}
                        selectedItems={selectedExternalDestinations}
                        title="Select Destination"
                      />
                    </Form>
                    <Form className="form-controls radio set-default" onSubmit={e => e.preventDefault()}>
                      <FormGroup>
                        <label className="nowrap">
                          Set this Data Delivery as default
                        </label>
                        <input
                          type="checkbox"
                          value="none"
                          checked={defaultDeliveryOption}
                          onChange={onSetDeliveryOptionDefault}
                          disabled={!access('order_edit')}
                        />
                      </FormGroup>
                    </Form>
                    <Modal centered backdrop
                      isOpen={(pendingExtDest || isMappingStatus) && missingDestinations?.length > 0}
                    >
                      <ModalHeader>
                        <i className="bi bi-exclamation-triangle-fill"></i>Missing External Destination
                      </ModalHeader>
                      <ModalBody>
                        <div>
                          One or more external destinations are no longer available. This order cannot be placed and needs to be re-created.
                        </div>
                        <Alert color="warning" className="d-flex">
                          <i className="bi bi-lightbulb"></i>
                          <div>
                            Consider going back to <strong>Explorer</strong> which will have this order's filters. From there a new order can be created and existing destinations can be mapped for order output.
                          </div>
                        </Alert>
                      </ModalBody>
                      <ModalFooter>
                        <Button onClick={(e) => { history.push("/orders") }}>Back to Orders</Button>
                        <Button className="promote" onClick={(e) => { history.push("/explorer") }}>View in Explorer</Button>
                      </ModalFooter>
                    </Modal>
                    {hasExternalDestinationDelivery && selectedExternalDestinations?.length > 0 && (
                      <>
                        <div className="ext-dest-table">
                          <Table>
                            <thead>
                              <tr>
                                <th className="name">External Destination</th>
                                <th className="desk">Description</th>
                                <th className="status">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedExternalDestinations?.length > 0 && selectedExternalDestinations.map((item, index) => {
                                return (
                                  <tr key={index}>
                                    <td>{item.name}</td>
                                    <td>{item.description}</td>
                                    <td>{renderDestinationStatus(item.id)}</td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </Table>
                        </div>
                      </>)}
                  </div>
                  <Alert
                    isOpen={dataFieldsMissing}
                    color="warning"
                    className="d-flex"
                  >
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    <div>
                      Since the last time this order was saved, one or more of the additional output data fields below are no longer available.<br /><strong>Please review the additional data fields below prior to ordering.</strong>
                    </div>
                  </Alert>
                  <DualSelect
                    name={"Additional Data Fields"}
                    optionLabel="label"
                    allOptions={allFields}
                    selectedOptions={selectedFields}
                    setSelectedOptions={setSelectedFields}
                    onChangeTemplate={onChangeTemplate}
                    displayTemplates
                    disabled={!access('order_edit')}
                    showSelectionOnly={!access('order_edit') || pendingExtDest || isMappingStatus}
                  />
                </CardBody>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  Set up External Destination Sync Mapping
                </CardHeader>
                <CardBody>
                  <OrderFinalizeExternalDestination
                    selectedDestinations={selectedExternalDestinations}
                    onCensusData={data => handleCensusData(data)}
                    onExit={handleExtDestExit}
                    order={originalOrder}
                    onMappingStatusChange={setHasSyncMappingChanges}
                  />
                </CardBody>
              </Card>
            )}
          </div>
        </section>
        {loaded &&
          <section className="order-overview">
            <h1>Order Summary</h1>
            <Card className="order-cart">
              <div className="total-cost">
                <FormatCurrency value={totalCost} />
              </div>
              <Table className="line-items">
                <thead>
                  <tr className="headings">
                    <th>Item</th>
                    <th>Channel(s)</th>
                    <th>Qty</th>
                    <th>@</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems &&
                    lineItems.map((item) => (
                      <tr key={item.invoice_category.id}>
                        <td>{item.invoice_category.name}</td>
                        <td>{
                          item.invoice_category.channel_options.map((option) => {
                            let selectedOnly = [];
                            let lowerName = option.name.toLowerCase();
                            let matchedKey = Object.keys(channel_option_name_match).find((_ch) => lowerName.startsWith(channel_option_name_match[_ch]));
                            Object.keys(channelOption).forEach((_ch) => {
                              if (channelOption[_ch]) {
                                selectedOnly.push(_ch);
                              }
                            });
                            /* only show channel names selected */
                            if (!selectedOnly.includes(matchedKey)) {
                              return "";
                            }
                            return <span key={option.id}><span>&bull; {option.name}</span><br /></span>
                          })
                        }</td>
                        <td>
                          <FormatNumber value={item.quantity} />
                        </td>
                        <td>
                          <FormatCurrency value={item.unit_price} />
                        </td>
                        <td>
                          <FormatCurrency value={item.total_price} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card>
            <div className="audience-summary">
              <Alert isOpen={exceedCapLimit} color={hasApprovedOrder ? "success" : "danger"} className="d-flex">
                <i className="bi bi-exclamation-triangle-fill"></i>
                <div>
                  Warning: This order exceeds the limits set by your company {!hasApprovedOrder && "and will require approval before it is officially placed"}
                  <br /><button type="button" className="see-more-details-sm" onClick={toggleOrderCapDetails}>
                    See More Details
                  </button>
                  {hasApprovedOrder && <span className="approved-text"><i className="bi bi-check" /> Approved</span>}
                </div>
              </Alert>
              <Alert isOpen={hasMyDataOnly} color="warning" className="d-flex">
                <i className="bi bi-exclamation-triangle-fill"></i>
                <div>
                  Note: Total records delivered may vary from quantity ordered due to data refreshes and updates made.
                </div>
              </Alert>
              <Alert isOpen={showCassMessage} color="warning" className="d-flex">
                <i className="bi bi-exclamation-octagon"></i>
                <div>
                  Warning: Your order contains records that might not have been processed through NCOA in the past 90 days. It is recommended to re-run processing before placing in the mail stream.
                </div>
              </Alert>
              <h2>Audience Summary</h2>
              <div className="summary">
                <div className="selection">
                  <div className="starting-quantity labeled-items horizontal center">
                    <div className="labeled-item center">
                      <label>Starting Quantity:</label>
                      <div className="value large">
                        <FormatNumber value={originalQuantity} />
                      </div>
                    </div>
                    <div className="labeled-item center">
                      <label>Dedupe Level:</label>
                      <div className="value large">
                        {session.crossfilterStore.dedupe}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="summary">
                <div className="criteria">
                  <Alert
                    isOpen={(dataFiltersHaveChanged) && getMaxQuantity() >= minQuantity && !noFilters}
                    color="warning"
                    className="d-flex"
                  >
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    <div>
                      Since the last time this order was saved, one or more of the filters chosen are no longer available. <strong>Please review the selection criteria below prior to ordering</strong>.
                    </div>
                  </Alert>
                  <h3>Selection Criteria</h3>
                  {!isComplex && activeFilters && activeFilters.map((chart_id) =>
                    <Chart summarize chart_id={chart_id} key={chart_id} viewOnly />
                  )}
                  {isComplex && complexFields && <button onClick={toggleModal}>Complex Filters</button>}
                </div>
              </div>
            </div>
            <div className="form-group column">
              <h3>Notes</h3>
              {access('order_edit') &&
                <textarea value={notes} onChange={onNotesChange}></textarea>
              }
              {!access('order_edit') &&
                <p>{notes || "No saved notes."}</p>
              }
            </div>
          </section>
        }
      </div>
      <Modal
        isOpen={errorModalIsOpen}
        onRequestClose={(e) => toggleModal(e)}
        className="modal-xl"
        ariaHideApp={false}
        centered
      >
        <div className="text-center p-3">
          <em>Complex Filters</em>
          <ComplexFilterBuilder
            initialQuery={
              isComplex && complexFilters && complexFilters.tree
                ? complexFilters.tree
                : null
            }
            fields={complexFields}
            readonly={true}
          />
          <Button onClick={toggleModal}>Close</Button>
        </div>
      </Modal>
      <ConfirmNoInvoice
        show={showNoInvoiceModal}
        onHide={() => setShowNoInvoiceModal(!showNoInvoiceModal)}
        onSubmit={onSubmitNoInvoice}
      />
      <ConfirmSuccess show={showSuccess} label={succesLabel} />
      <Modal centered backdrop
        isOpen={unsavedModalIsOpen && !noFilters}
        toggle={() => toggleUnsavedModal()}
      >
        <ModalHeader toggle={() => toggleUnsavedModal()}>
          Unsaved Order
        </ModalHeader>
        <ModalBody>
          You have unsaved changes to this order. If you leave the page before saving, your changes will be lost.
        </ModalBody>
        <ModalFooter>
          <Button className="promote" onClick={toggleUnsavedModal}>Cancel</Button>
          <Button onClick={continueWithoutSaving}>Continue</Button>
        </ModalFooter>
      </Modal>

      <Modal centered backdrop isOpen={showNeedLicense}>
        <ModalHeader>
          No License Fee Found
        </ModalHeader>
        <ModalBody>
          A License Fee is needed to place orders.
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => setShowNeedLicense(false)}>Close</Button>
        </ModalFooter>
      </Modal>
      <HasLicenseFee hasLicense={hasLicense} />
      <Modal centered backdrop
        isOpen={originalOrderBelowMin}
      >
        <ModalHeader>
          <i className="bi bi-exclamation-triangle-fill"></i>
          Invalid Order Quantity
        </ModalHeader>
        <ModalBody>
          The quantity of this audience has changed since it was last saved and now falls below the minimum order threshold.
          <ul>
            <li>
              Last saved desired quantity: <strong>{originalOrder.quantity}</strong>.
            </li>
            <li>
              Current available quantity: <strong>{getMaxQuantity()}</strong>.
            </li>
          </ul>
          To resolve this issue, return to <strong>Explorer</strong>, adjust the audience filters, and <strong>place a new order from the menu.</strong>
        </ModalBody>
        <ModalFooter>
          <Button onClick={(e) => { history.push("/orders") }}>Back to Orders</Button>
          <Button className="promote" onClick={(e) => { history.push("/explorer") }}>Open Explorer</Button>
        </ModalFooter>
      </Modal>
      <Modal centered backdrop
        isOpen={dataFiltersHaveChanged && noFilters}
      >
        <ModalHeader>
          <i className="bi bi-exclamation-triangle-fill"></i>
          No Filters in this Order
        </ModalHeader>
        <ModalBody>
          Since the last time this order was saved, the filters used are no longer available. Please open the <strong>Explorer</strong> page and create a new order.
        </ModalBody>
        <ModalFooter>
          <Button onClick={(e) => { history.push("/orders") }}>Back to Orders</Button>
          <Button className="promote" onClick={(e) => { history.push("/explorer") }}>Open Explorer</Button>
        </ModalFooter>
      </Modal>

      <Modal centered backdrop className="modal-lg" isOpen={showOrderCapDetails}>
        <ModalHeader>
          <i className="bi bi-exclamation-triangle-fill"></i>
          Order Exceeds Limits
        </ModalHeader>
        <ModalBody>
          <div className="mb-2">This order exceeds the limits set by your company and will require approval before it is officially placed.</div>
          <div className="mb-2">
            {(orderApproversInfo.length > 0) && `Designated approvers include:`}
            {orderApproversInfo.map(person => ` ${person.first_name} ${person.last_name}`).join(', ')}
          </div>
          <div className="mb-3">Please contact Customer Success with questions.</div>
          <h5>Limits Exceeded</h5>
          <div>
            <table className="table table-striped">
              <tbody>
                {orderLimitsInfo.map((item) => (
                  <tr key={item.order_cost_limit_period_id}>
                    <td>Limit period: <b>{order_limits_enum[item.order_cost_limit_period_id]}</b></td>
                    <td>Limit value: $<b>{item.dollar_limit}</b></td>
                    <td>Limit exceeded by: $<b>{item.excess_amount}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isOrderApprover && originalOrder.status !== "Pending Approval" ? (
            <div className="form-check mt-4">
              <input
                className="form-check-input"
                type="checkbox"
                onChange={handleApproveCheckbox}
                checked={hasAcknowledgeCheck}
                disabled={hasApprovedOrder}
              />
              <label className="form-check-label">
                I understand that this order exceeds the limits set by the company and I wish to<br />
                <b>approve and proceed with the order</b>.
              </label>
            </div>
          ) : ""}
        </ModalBody>
        <ModalFooter>
          {hasApprovedOrder ? (<Button className="" onClick={handleRemoveApproval}>Remove Approval</Button>) : ""}
          {isOrderApprover && originalOrder.status !== "Pending Approval" ? (
            <Button className="" onClick={handleApproveOrder} disabled={!hasAcknowledgeCheck || hasApprovedOrder}>Approve Order</Button>
          ) : ""}
          <Button className="promote" onClick={toggleOrderCapDetails}>Close</Button>
        </ModalFooter>
      </Modal>

      <Modal centered backdrop isOpen={externalDestinationNotAvailable}>
        <ModalHeader>
          External Destination Not Available
        </ModalHeader>
        <ModalBody>
          <Alert color="warning" className="d-flex">
            <i className="bi bi-exclamation-triangle-fill"></i>
            The External Destination you had selected is no longer available. Please choose another file delivery option.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button className="promote" onClick={() => setExternalDestinationNotAvailable(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      <Modal centered backdrop isOpen={confirmLeaveSyncModal} toggle={() => setConfirmLeaveSyncModal(false)}>
        <ModalHeader toggle={() => setConfirmLeaveSyncModal(false)}>
          <i className="bi bi-exclamation-triangle me-2" />
          Unsaved Mapping Changes
        </ModalHeader>
        <ModalBody>
          You have unsaved field mapping changes. If you leave, your mapping changes will be lost.
        </ModalBody>
        <ModalFooter>
          <Button className="promote" onClick={() => setConfirmLeaveSyncModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmLeaveSync}>
            Continue
          </Button>
        </ModalFooter>
      </Modal>

      <ReviewOrderApproval
        show={showReviewApproval}
        onClose={handleShowReviewApprovalModal}
        onDeny={handleDenyOrder}
        onApprove={handleApproveSubmitOrder}
      />
    </div>
  );
})

export default OrderForm;

/**
 * Input for reason to Do Not Invoice.
 */
export function ConfirmNoInvoice(props) {
  const { show = false, onHide, onSubmit } = props;
  const [dropdownChoice, setDropdownChoice] = useState("");
  const [textValue, setTextValue] = useState("");
  const [showOtherArea, setShowOtherArea] = useState(false);
  const [reason, setReason] = useState("");
  const [disableSave, setDisableSave] = useState(true);

  const options = [
    "Test file for internal Quality Assurance purposes",
    "Data Evaluations",
    "Order needs additional processing by Operations",
    "Data issue - data output did not meet requirements",
    "Duplicate order",
    "Order placed under wrong company",
    "Usage to be reported",
    "Other (explain)",
  ];

  const handleTextAreaChange = (event) => {
    setTextValue(event.target.value);
  };

  const handleSave = () => {
    onSubmit(reason);
  }

  const handleDropdownChoice = (choice) => {
    setDropdownChoice(choice);
  }

  useEffect(() => {
    // ask for 'other' details
    if (dropdownChoice.includes("(explain)")) {
      setDisableSave(true);
      setShowOtherArea(true);
      setReason(textValue);
      if (textValue.length > 24 && textValue.length < 201) {
        setDisableSave(false);
      }
    } else {
      setShowOtherArea(false);
      setReason(dropdownChoice);
      if (dropdownChoice.length > 3) {
        setDisableSave(false);
        setTextValue("");
      }
    }
  }, [dropdownChoice, textValue])

  const handleCancel = () => {
    setTextValue("");
    setDropdownChoice("");
    setDisableSave(true);
    onHide();
  }

  return (
    <Modal isOpen={show} className="reason-modal">
      <div className="modal-header">
        <h4>Reason for Not Invoicing Order:</h4>
      </div>

      <div className="modal-body reason-textarea">
        <SimpleDropdown
          label=" "
          options={options}
          selectedValue={handleDropdownChoice}
          customCss="do-not-invoice-dropdown"
        />
        {showOtherArea && <>
          <textarea
            value={textValue}
            onChange={handleTextAreaChange}
            rows={8}
            placeholder="Type your explanation here..."
          />
          <em>*Please enter between 25 and 200 characters.</em>
        </>}
      </div>

      <div className="modal-footer">
        <Button
          type="button"
          onClick={handleCancel}
        >Cancel</Button>
        <Button
          type="button"
          className="promote"
          onClick={handleSave}
          disabled={disableSave}
        >
          Do Not Invoice
        </Button>
      </div>
    </Modal>
  );
}


/**
 * priority radio options
 */
const CutbackPriorityGroup = ({ onSelection, data, desiredQuantity, originalQuantity, onChanges, _filtersInfo, _session }) => {
  const onSelectionChange = onSelection;
  const savedOrderInfo = data;
  const session = _session;
  const filtersInfoRef = useRef(_filtersInfo);

  const [filtersInfo, setFiltersInfo] = useState();
  const [cutbackSelection, setCutbackSelection] = useState("Random");
  const [availableOptions, setAvailableOptions] = useState([]);
  const [showModelsList, setShowModelsList] = useState(false);
  const [showFiltersList, setShowFiltersList] = useState(false);
  const [selectedModel, setSelectedModel] = useState();
  const [tempSelectedFilter, setTempSelectedFilter] = useState();
  const [tempSelectedFilterFinal, setTempSelectedFilterFinal] = useState();
  const [companyModelDatasetId, setCompanyModelDatasetId] = useState();
  const [selectedRadio, setSelectedRadio] = useState("random");
  const [hasOneModel, setHasOneModel] = useState();
  const [loaded, setLoaded] = useState(false);
  const [filterValues, setFilterValues] = useState();
  const [allFiltersInfo, setAllFiltersInfo] = useState();
  const [allFiltersLabels, setAllFiltersLabels] = useState();
  const [allModelsLabels, setAllModelsLabels] = useState();
  const [currentSelection, setCurrentSelection] = useState();

  useEffect(() => {
    filtersInfoRef.current.then((res) => {
      setFiltersInfo(res);
    })
  }, [])

  const handleChange = (val) => {
    // if "random" return empty array: cutback_fields: []
    if (val === "random") {
      onSelectionChange([]);
      setSelectedRadio(val);
      setCutbackSelection("Random");
      clearAllTempFilterValues();
      setCurrentSelection(null);
      return;
    }

    /* if "model_ranking" return which model was selected: */
    if (val === "model_ranking") {
      if (hasOneModel) {
        setSelectedRadio(val);
        setSelectedModel(hasOneModel);
        handleModelSelection();
        clearAllTempFilterValues();
        setCutbackSelection(`Model: ${hasOneModel.label}`);
        return;
      }
      setSelectedModel("");
      setShowModelsList(true);
      return;
    }

    /* if "filters_and_insights": allow user to rearrange */
    if (val === "filters_and_insights") {
      setShowFiltersList(true);
    }
  };

  const handleModelChoice = (_model) => {
    filtersInfo.all_models.forEach((val) => {
      if (val.label === _model) {
        setSelectedModel(val);
      }
    })
  }

  const handleModelSelection = () => {
    setSelectedRadio("model_ranking");

    let model_id = (hasOneModel && hasOneModel.id) ? hasOneModel.id : (selectedModel && selectedModel.id) || "";
    let format_output = [{ ds: companyModelDatasetId, id: model_id, value: "1", value2: "20" }];
    setCurrentSelection(format_output);
    onSelectionChange(format_output);
    let _label = "";
    if (selectedModel && selectedModel.label) {
      _label = selectedModel.label;
    } else if (hasOneModel && hasOneModel.label) {
      _label = hasOneModel.label;
    }
    setCutbackSelection(`Model: ${_label}`);
    clearAllTempFilterValues();
    setShowModelsList(false);
  }

  const callHandleModelSelection = (_model) => {
    setSelectedRadio("model_ranking");
    setSelectedModel(_model);
  };

  useEffect(() => {
    if (selectedModel) {
      handleModelSelection();
    }
    // eslint-disable-next-line
  }, [selectedModel])

  /* filters functions */

  const clearAllTempFilterValues = () => {
    setTempSelectedFilterFinal("");
    setTempSelectedFilter("");
    setFilterValues([]);
  }

  const handleFilterChoice = (_filter) => {
    setTempSelectedFilter(_filter);
  }

  useEffect(() => {
    if (tempSelectedFilter) {
      let values = [];
      allFiltersInfo.forEach((item) => {
        if (item.label === tempSelectedFilter) {
          if (tempSelectedFilter === "County" ||
            tempSelectedFilter === "Congressional District" ||
            tempSelectedFilter === "CBSA" ||
            tempSelectedFilter === "State") {
            Object.entries(item.friendly_names).forEach((_val) => {
              values.push(_val[1]);
            })
          } else {
            Object.entries(item.values).forEach((_val) => {
              values.push(_val[1]);
            })
          }
        }
      });
      setFilterValues(values);
    }
    // eslint-disable-next-line
  }, [tempSelectedFilter])

  useEffect(() => {
    if (tempSelectedFilterFinal && tempSelectedFilterFinal.length) {
      let sortedValues = tempSelectedFilterFinal.map(item => {
        let key = Object.keys(item.values)[0];
        return item.values[key];
      });
      if (filterValues && !filterValues.every((value, index) => value === sortedValues[index])) {
        setFilterValues(sortedValues);
      }
    }
    // eslint-disable-next-line
  }, [tempSelectedFilterFinal])

  const getAllFiltersMatch = (_labels, _filterName = tempSelectedFilter) => {
    if (!_labels) { return; }
    if (!filtersInfo.all_filters) {
      console.log("No filters info", filtersInfo.all_filters)
      return;
    }

    let _ds = "";
    let _id = "";
    let _values = [];

    if (_filterName === "County" || _filterName === "Congressional District" ||
      _filterName === "CBSA" || _filterName === "State") {
      filtersInfo.all_filters.forEach((item) => {
        if (item.label === _filterName) {
          _ds = item.ds;
          _id = item.id;
          item.friendly_names && Object.entries(item.friendly_names).forEach((val) => {
            if (_labels.includes(val[0])) {
              _values.push({ [val[0]]: val[1] });
            } else if (_labels.includes(val[1])) {
              _values.push({ [val[0]]: val[1] });
            }
          })
        }
      })
    } else {
      filtersInfo.all_filters.forEach((item) => {
        if (item.label === _filterName) {
          _ds = item.ds;
          _id = item.id;
          item.values && Object.entries(item.values).forEach((_val) => {
            _values.push({ [_val[0]]: _val[1] })
          })
        }
      });
    }

    // sort _values based on _labels
    if (_filterName === "County" || _filterName === "Congressional District" ||
      _filterName === "CBSA" || _filterName === "State") {
      // geo widgets sort differently
      _values.sort((a, b) => {
        const keyA = Object.keys(a)[0];
        const keyB = Object.keys(b)[0];
        return _labels.indexOf(keyA) - _labels.indexOf(keyB);
      });
    } else {
      const labelIndexMap = _labels.reduce((acc, label, index) => {
        acc[label.trim()] = index;
        return acc;
      }, {});

      _values.sort((a, b) => {
        const labelA = Object.values(a)[0].trim();
        const labelB = Object.values(b)[0].trim();
        return labelIndexMap[labelA] - labelIndexMap[labelB];
      });
    }

    // collect all info and format for output
    let filter_output = [];
    _labels.forEach((item) => {
      _values && _values.forEach((val) => {
        let this_key = Object.keys(val);
        if (val[this_key] === item || this_key[0] === item) {
          filter_output.push({ ds: _ds, id: _id, values: val });
        }
      })
    })

    return filter_output;
  }

  const handleFilterSelection = () => {
    setCutbackSelection(`Filter: ${tempSelectedFilter}`);
    setSelectedRadio("filters_and_insights");
    let format_output = [];
    tempSelectedFilterFinal && tempSelectedFilterFinal.forEach((item) => {
      format_output.push({ ds: item.ds, id: item.id, value: Object.entries(item.values)[0][0], value2: "" })
    })
    setCurrentSelection(format_output);
    onSelectionChange(format_output)
    setShowFiltersList(false);
  }

  const handleFiltersPriority = (_list, _filterName) => {
    if (_list) {
      let _data = getAllFiltersMatch(_list, _filterName);
      setTempSelectedFilterFinal(_data);
    }
  }

  useEffect(() => {
    !currentSelection && (selectedRadio === "filters_and_insights") && onSelection(tempSelectedFilterFinal);
    // eslint-disable-next-line
  }, [tempSelectedFilterFinal])

  const onEditSelection = () => {
    let split_name = cutbackSelection ? cutbackSelection.split(":")[0] : "";
    if (split_name === "Filter") {
      setShowFiltersList(true);
      return;
    }
    if (split_name === "Model") {
      setShowModelsList(true);
      return;
    }
  }

  useEffect(() => {
    let temp_comp_code = session.appState.activeCompany.code || null;
    let _population_domain = session.crossfilterStore.explorerModule || "consumers";
    const initRun = () => {
      setCompanyModelDatasetId(`${session.appState.activeCompany.code}_models_${_population_domain}_app`);

      let options = [];

      session.query("reference/get_cutback_options").then((_options) => {
        if (_options && _options.length) {
          _options.forEach((val) => {
            options.push({ label: val.name, value: val.name.toLowerCase().trim().replace(/\s+/g, '_').replace(/&/g, 'and'), message: val.description });
          })
        }
      }).then(() => {
        let allFilters = filtersInfo.all_filters || [];
        let allModels = filtersInfo.all_models || [];

        setAllFiltersInfo(filtersInfo.all_filters);
        setAllFiltersLabels(filtersInfo.filters_labels);
        setAllModelsLabels(filtersInfo.models_labels)

        // display/hide radio options
        const noFilters = options.filter((val) => val.value !== "filters_and_insights" && val.value !== "random");
        const multipleModels = options.filter((val) => val.value !== "filters_and_insights");
        const noModel = options.filter((val) => val.value !== "model_ranking");

        if (allModels.length) {
          // if only one model exist and no other filters: select it
          if ((allModels.length === 1) && (allFilters.length === 0)) {
            setAvailableOptions(noFilters);
            callHandleModelSelection(allModels[0])
            setSelectedRadio("model_ranking");
            setCutbackSelection(`Model: ${allModels[0].label}`);
          } else if ((allModels.length === 1)) {
            // if only one model exist, but also other filters: auto selected when user clicks the radio button
            setHasOneModel(allModels[0]);
            setAvailableOptions(options);
          } else {
            // if multiple models and no other filters
            setSelectedModel("");
            !allFilters.length ? setAvailableOptions(multipleModels) : setAvailableOptions(options);
          }
        } else if (!availableOptions.length) {
          setAvailableOptions(noModel);
        }
      }).then(() => {
        // handle loading a saved order
        if (savedOrderInfo && savedOrderInfo.length) {
          /* set the selected model */
          let isModel = savedOrderInfo[0].ds.includes(`_models_${_population_domain}_app`);
          if (isModel) {
            filtersInfo.all_models.forEach((val) => {
              if (val.id === savedOrderInfo[0].id) {
                callHandleModelSelection(val);
                setCutbackSelection(`Model: ${val.label}`);
                setCurrentSelection(savedOrderInfo[0]);
              }
            })
          } else { /* set the selected filter */
            filtersInfo.all_filters.forEach((val) => {
              if (val.id === savedOrderInfo[0].id) {
                setSelectedRadio("filters_and_insights");
                setTempSelectedFilter(val.label);
                setCutbackSelection(`Filter: ${val.label}`);
                let sorted_labels = [];
                savedOrderInfo.forEach((item) => {
                  sorted_labels.push(val.values[item.value]);
                })
                handleFiltersPriority(sorted_labels, val.label);
                setCurrentSelection(savedOrderInfo);
                onSelection(savedOrderInfo);
              }
            })
          }
        } else {
          setCurrentSelection(null);
        }
      })
        .catch((err) => console.log("Failed to load cutback options: ", err))
    }

    if (!loaded && filtersInfo) {
      initRun();
    }

    if (temp_comp_code && filtersInfo) {
      setLoaded(true);
    } else {
      setLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedOrderInfo, filtersInfo])

  useEffect(() => {
    if (loaded && savedOrderInfo) {
      switch (selectedRadio) {
        case "model_ranking":
          if ((selectedModel && savedOrderInfo.length) && isEqual(savedOrderInfo[0].id, selectedModel.id)) {
            onChanges(false); // same as saved
          } else {
            onChanges(true);
          }
          break;

        case "filters_and_insights":
          if (isEqual(savedOrderInfo, currentSelection)) {
            onChanges(false); // same as saved
          } else {
            onChanges(true);
          }
          break;

        case "random":
          if (!savedOrderInfo.length) {
            onChanges(false); // same as saved
          } else {
            onChanges(true);
          }
          break;

        default:
          onChanges(false);
          break;
      }

      /* handle new orders */
      if (!savedOrderInfo) {
        onChanges(true);
      }
    } else {
      isNaN(desiredQuantity) || (Number(desiredQuantity) >= Number(originalQuantity)) ? onChanges(false) : onChanges(savedOrderInfo !== currentSelection);
    }
    // eslint-disable-next-line
  }, [selectedRadio, currentSelection, selectedModel, savedOrderInfo, loaded])

  if (!loaded) { return "" }

  if (isNaN(desiredQuantity) || (Number(desiredQuantity) >= Number(originalQuantity))) {
    return "";
  }

  return (
    <div className="bs-form mt-4">
      <Label>Cutback Options:</Label>
      <div className="cutback-radio-wrapper">
        {availableOptions.length ? availableOptions.map((option) => (
          <div key={option.label}>
            <div className="order-radio-wrapper" onClick={() => handleChange(option.value)}>
              {selectedRadio === option.value ? (
                <span className="radio-base radio-selected" />) : (<span className="radio-base radio-not-selected" />
              )}
              <span className="order-radio">{option.label}&nbsp;</span>
            </div>
            <InfoPopover
              title={option.label}
              message={option.message}
              offsetTop={-2}
            />
          </div>
        )) : ""}
      </div>
      <div className="selected-cutback-details">
        {(cutbackSelection && cutbackSelection !== "Random") && <>
          <span className="selection-details">Selected {cutbackSelection}</span>
          <button type="button" className="edit-priority-selection" onClick={onEditSelection}>
            Edit/Change
          </button>
        </>}
      </div>
      <Modal isOpen={showModelsList} className="reason-modal">
        <div className="modal-header">
          <h4>Select A Model:</h4>
        </div>
        <div className="m-3">
          <Alert isOpen color="light" className="d-flex">
            <i className="bi bi-info-circle-fill"></i>
            <div>
              Select which model to use to prioritize your desired quantity.
            </div>
          </Alert>
        </div>
        <div className="modal-body reason-textarea">
          <SimpleDropdown
            label=" "
            options={allModelsLabels}
            selectedValue={handleModelChoice}
            customCss="do-not-invoice-dropdown"
            preset={(selectedModel && selectedModel.label) ? selectedModel.label : null}
          />
        </div>
        <div className="modal-footer">
          <Button type="button" onClick={() => setShowModelsList(false)}>Cancel</Button>
          <Button type="button" className="promote" onClick={handleModelSelection} disabled={!selectedModel}>
            Select
          </Button>
        </div>
      </Modal>

      <Modal isOpen={showFiltersList} className="reason-modal">
        <div className="modal-header">
          <h4>Select Your Filter or Insight:</h4>
        </div>
        <div className="m-3">
          <Alert isOpen color="light" className="d-flex">
            <i className="bi bi-info-circle-fill"></i>
            <div>
              Select which filter or insight to use. Drag the values to prioritize your desired quantity.
            </div>
          </Alert>
          <div className="modal-body reason-textarea">
            <SimpleDropdown
              label=" "
              options={allFiltersLabels}
              selectedValue={handleFilterChoice}
              customCss="do-not-invoice-dropdown"
              preset={tempSelectedFilter || null}
            />
            <div>
              {filterValues && <DraggableList items={filterValues} onListChange={handleFiltersPriority} />}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <Button type="button" onClick={() => setShowFiltersList(false)}>Cancel</Button>
          <Button type="button" className="promote" onClick={handleFilterSelection} disabled={!tempSelectedFilter}>
            Update
          </Button>
        </div>
      </Modal>
    </div>
  );
};

/**
 * approve or deny the current order
 */
const ReviewOrderApproval = ({ show, onClose, onDeny, onApprove }) => {
  const [denialReason, setDenialReason] = useState("");
  const [denialDisabled, setDenialDisabled] = useState(true);

  const handleApprove = () => {
    onApprove();
    handleClose();
  };

  const handleDeny = () => {
    if (!denialReason.trim()) {
      return;
    }
    onDeny(denialReason);
    handleClose();
  };

  const handleClose = () => {
    setDenialReason("");
    onClose();
  }

  useEffect(() => {
    if (!denialReason.trim()) {
      setDenialDisabled(true);
    } else {
      setDenialDisabled(false);
    }
  }, [denialReason])

  return (
    <Modal size="lg" centered isOpen={show} backdrop>
      <ModalHeader>Review Order</ModalHeader>
      <ModalBody>
        <ul>
          <li>If you approve, the order will be placed immediately.</li>
          <li>If you deny, please provide a reason for the denial.</li>
        </ul>
        <div>
          <label htmlFor="denialReason">Reason for Denial:</label>
          <Input
            type="textarea"
            id="denialReason"
            value={denialReason}
            onChange={(e) => setDenialReason(e.target.value.trimStart())}
            placeholder="Enter your reason here..."
            rows={5}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button onClick={handleClose}>Close</Button>
        <Button className={" promote"} onClick={handleDeny} disabled={denialDisabled}>Deny</Button>
        <Button className={!denialReason.trim() ? " promote" : ""} onClick={handleApprove}>
          Approve and Place Order
        </Button>
      </ModalFooter>
    </Modal>
  );
};
